import { useState, useEffect } from 'react';

export const useSensors = () => {
  const [sensors, setSensors] = useState({
    accelerometer: null,
    gyroscope: null,
    gps: null,
    wifi: null
  });
  const [permission, setPermission] = useState('prompt');

  useEffect(() => {
    initializeSensors();
  }, []);

  const initializeSensors = async () => {
    if (typeof DeviceMotionEvent.requestPermission === 'function') {
      try {
        const motionPermission = await DeviceMotionEvent.requestPermission();
        const orientationPermission = await DeviceOrientationEvent.requestPermission();
        setPermission(motionPermission === 'granted' ? 'granted' : 'denied');
        if (motionPermission === 'granted') {
          startMotionSensors();
        }
      } catch (error) {
        console.log('Sensor permission API not supported');
        startMotionSensors();
      }
    } else {
      startMotionSensors();
    }
    startGPSSensor();
    startWiFiSensor();
  };

  const startMotionSensors = () => {
    window.addEventListener('devicemotion', (event) => {
      setSensors(prev => ({
        ...prev,
        accelerometer: {
          x: event.accelerationIncludingGravity?.x || 0,
          y: event.accelerationIncludingGravity?.y || 0,
          z: event.accelerationIncludingGravity?.z || 0,
          timestamp: Date.now()
        }
      }));
    });
    window.addEventListener('deviceorientation', (event) => {
      setSensors(prev => ({
        ...prev,
        gyroscope: {
          alpha: event.alpha || 0,
          beta: event.beta || 0,
          gamma: event.gamma || 0,
          timestamp: Date.now()
        }
      }));
    });
  };

  const startGPSSensor = () => {
    if (navigator.geolocation) {
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          setSensors(prev => ({
            ...prev,
            gps: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              altitude: position.coords.altitude,
              speed: position.coords.speed,
              heading: position.coords.heading,
              timestamp: Date.now()
            }
          }));
        },
        (error) => {
          console.error('GPS error:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
      return () => navigator.geolocation.clearWatch(watchId);
    }
  };

  const startWiFiSensor = () => {
    const simulateWiFi = () => {
      setSensors(prev => ({
        ...prev,
        wifi: {
          signals: [
            { ssid: 'Home_WiFi', strength: -45, frequency: 2.4 },
            { ssid: 'Cafe_Network', strength: -65, frequency: 5.0 },
            { ssid: 'Office_WiFi', strength: -55, frequency: 2.4 }
          ],
          timestamp: Date.now()
        }
      }));
    };
    simulateWiFi();
    const interval = setInterval(simulateWiFi, 5000);
    return () => clearInterval(interval);
  };

  const getEnhancedLocation = () => {
    const gps = sensors.gps;
    const accelerometer = sensors.accelerometer;
    if (!gps) return null;
    
    let enhancedAccuracy = gps.accuracy || 1000;
    let enhancedSpeed = gps.speed || 0;
    
    if (accelerometer) {
      const movement = Math.sqrt(
        accelerometer.x ** 2 + accelerometer.y ** 2 + accelerometer.z ** 2
      );
      if (movement > 0.3) {
        enhancedAccuracy = Math.max(5, gps.accuracy * 0.7);
        if (enhancedSpeed < 1 && movement > 0.5) {
          enhancedSpeed = movement * 10;
        }
      }
    }
    
    return {
      latitude: gps.latitude,
      longitude: gps.longitude,
      speed: enhancedSpeed * 3.6,
      heading: gps.heading || 0,
      accuracy: enhancedAccuracy,
      altitude: gps.altitude,
      sensorEnhanced: true,
      sensors: {
        accelerometer: !!sensors.accelerometer,
        gyroscope: !!sensors.gyroscope,
        wifi: !!sensors.wifi
      },
      timestamp: gps.timestamp
    };
  };

  return {
    sensors,
    permission,
    getEnhancedLocation,
    requestPermission: initializeSensors
  };
};